import React, { useState, useEffect } from 'react';

/**
 * Settings Modal Component
 * Allows users to customize which tabs are visible in their dashboard
 * By default, new users see "Map", "Smart Notifications", and "Public Buses"
 */
const SettingsModal = ({ isOpen, onClose, onSettingsChange }) => {
  const [tabSettings, setTabSettings] = useState({
    map: true, // Always enabled, can't be disabled
    'enhanced-subs': true, // Always enabled, can't be disabled
    vehicles: false,
    shared: false,
    buses: true, // Automatically enabled for all users
    subscriptions: false,
    ads: false
  });

  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('userTabSettings');
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        // Ensure buses is always enabled (migration for existing users)
        const migratedSettings = {
          ...parsed,
          buses: true // Override any saved value to ensure buses is always enabled
        };
        setTabSettings(prev => ({
          ...prev,
          ...migratedSettings
        }));
        // Save the migrated settings back to localStorage
        localStorage.setItem('userTabSettings', JSON.stringify(migratedSettings));
      } catch (error) {
      }
    }
  }, []);

  const handleToggleTab = (tabId) => {
    // Don't allow disabling Map or Smart Notifications
    if (tabId === 'map' || tabId === 'enhanced-subs') {
      return;
    }

    setTabSettings(prev => {
      const newSettings = {
        ...prev,
        [tabId]: !prev[tabId]
      };
      
      // Save to localStorage
      localStorage.setItem('userTabSettings', JSON.stringify(newSettings));
      
      // Notify parent component
      onSettingsChange(newSettings);
      
      return newSettings;
    });
  };

  const handleSave = () => {
    onClose();
  };

  const handleReset = () => {
    const defaultSettings = {
      map: true,
      'enhanced-subs': true,
      vehicles: false,
      shared: false,
      buses: true, // Automatically enabled for all users
      subscriptions: false,
      ads: false
    };

    setTabSettings(defaultSettings);
    localStorage.setItem('userTabSettings', JSON.stringify(defaultSettings));
    onSettingsChange(defaultSettings);
  };

  if (!isOpen) return null;

  const tabOptions = [
    { id: 'map', label: 'Map', icon: '🗺️', description: 'Live garbage truck map with GPS tracking', required: true },
    { id: 'enhanced-subs', label: 'Garbage Truck Arriving', icon: '🚛', description: 'Get notified when garbage trucks approach', required: true }
    // Removed for garbageTracker: van-eta, vehicles, shared, buses, ads
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Customize Your Dashboard</h3>
          <p className="text-gray-600 text-sm mb-4">
            Configure your garbage truck tracking experience.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-blue-800 mb-1">💡 How It Works</h4>
            <p className="text-sm text-blue-700">
              Use the Map to see live garbage truck locations and set up Alerts to get notified when trucks are approaching your area.
            </p>
          </div>
        </div>

        <div className="space-y-3 mb-8">
          {tabOptions.map((option) => (
            <div 
              key={option.id}
              className={`flex items-center p-4 border rounded-lg ${
                tabSettings[option.id] 
                  ? 'border-green-200 bg-green-50' 
                  : 'border-gray-200 bg-gray-50'
              } ${option.required ? 'opacity-75' : ''}`}
            >
              <div className="flex items-center flex-1">
                <span className="text-2xl mr-3">{option.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center">
                    <h4 className="font-medium text-gray-900">{option.label}</h4>
                    {option.required && (
                      <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">Required</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </div>
              </div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={tabSettings[option.id]}
                  onChange={() => handleToggleTab(option.id)}
                  disabled={option.required}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="sr-only">
                  {tabSettings[option.id] ? 'Disable' : 'Enable'} {option.label}
                </span>
              </label>
            </div>
          ))}
        </div>

        <div className="flex justify-between">
          <button
            onClick={handleReset}
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
          >
            Reset to Default
          </button>
          <div className="space-x-3">
            <button
              onClick={onClose}
              className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;